import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '~/common/stores';
import dialog from '~/common/utils/dialog.util';
import { globalErrorHandler } from '~/common/utils/error-handler.util';
import { STORE_CONSOLE_ROLES } from '~/features/admin/data/admin.data.ts';
import { postSigninApi } from '~/features/auth/api/post-signin.api';
import {
  type ChangePasswordRequest,
  putMePasswordApi,
} from '~/features/auth/api/put-me-password.api';
import { fetchMe, resetAuthUser } from '~/features/auth/stores/auth.slice';
import { resetAuthData, setAuthData } from '~/features/auth/utils/auth.util';

import type { SigninRequest } from '~/features/auth/types/auth.type';

function useAuth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useAppSelector(state => state.auth);

  /**
   * 현재 로그인한 관리자 정보 조회
   */
  const fetchAuthUser = async () => {
    try {
      await dispatch(fetchMe()).unwrap();
    } catch {
      handleSignOut();
    }
  };

  /**
   * 로그인
   */
  const handleSignIn = async (body: SigninRequest) => {
    try {
      const { data } = await postSigninApi(body);
      setAuthData(data.data.accessToken);
      const me = await dispatch(fetchMe()).unwrap();
      navigate(STORE_CONSOLE_ROLES.includes(me.role) ? '/store' : '/');
    } catch (err) {
      await globalErrorHandler(err, {
        title: '로그인 오류',
        disableAuthCheck: true,
      });
    }
  };

  /**
   * 로그아웃
   */
  const handleSignOut = () => {
    resetAuthData();
    dispatch(resetAuthUser());
    navigate('/auth/sign-in');
  };

  /**
   * 비밀번호 변경
   */
  const handleChangePassword = async (body: ChangePasswordRequest): Promise<boolean> => {
    try {
      await putMePasswordApi(body);
      await dialog.message({
        title: '비밀번호 변경 완료',
        content: '비밀번호가 성공적으로 변경되었습니다.',
      });
      return true;
    } catch (err) {
      await globalErrorHandler(err, { title: '비밀번호 변경 오류' });
      return false;
    }
  };

  return {
    state,
    fetchAuthUser,
    handleSignIn,
    handleSignOut,
    handleChangePassword,
  };
}

export default useAuth;
